import React, { useEffect, useState } from "react";
import { Container } from "reactstrap";
import Section from "./Section";
import { useNavigate, useParams } from "react-router-dom";
import PageLoadingSpinner from "components/common/PageLoader";
import BreadCrumb from "components/common/BreadCrumb";
import type { Client } from "../types/client.types";
import ClientOverviewContext from "./ClientOverviewContext";
import { getDocumentTitle } from "utils/helpers";
import { getClient } from "../services/ClientService";
import { toast } from "react-toastify";

const ClientOverview = () => {
  document.title = getDocumentTitle("Client details");

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        const res = await getClient(Number(id));
        if (cancelled) return;
        if (res.success && res.data) {
          setClient(res.data);
        } else {
          toast.error(res.message || "Client not found");
          navigate("/clients");
        }
      } catch (err) {
        if (!cancelled) {
          toast.error((err as Error)?.message || "Failed to load client");
          navigate("/clients");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading) {
    return <PageLoadingSpinner message="Loading client..." />;
  }

  if (!client) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Client details"
          pageTitle="Customers"
          items={[{ label: "Clients", path: "/clients" }]}
        />
        <ClientOverviewContext.Provider value={{ client }}>
          <Section />
        </ClientOverviewContext.Provider>
      </Container>
    </div>
  );
};

export default ClientOverview;
